import { Button, capitalize, Stack } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { DataRequestState, useDataRequest } from "../data";
import { useGetApi } from "../../client";
import { EingangsrechnungInfo } from "../../api";
import Guard from "../Guard";
import ZahllaufSplitbutton from "./ZahllaufSplitbutton";
import { useZahllaufContext } from "../../contexts/ZahllaufContext";
import {
  RomeisDataGrid,
  ColumnVisibilityModel,
  ColumnFilter,
  SortModel,
  germanLabels,
  QueryResult,
  QueryOptions,
  ColumnDef,
} from "@romeis/romeis-data-grid";
import {
  ZahllaufFilter,
  ZahllaufProps,
  ZahllaufSummenProps,
  ZahllaufFilterBar,
  useZahllaufColumns,
} from "./ZahllaufCommon";
import { useT } from "../../i18n";
import { useNavigate } from "react-router";

export default function ZahllaufDataTable({
  setAnzahlAlleRechnungen,
  setSummeAlleRechnungen,
  setAnzahlSelectedRechnungen,
  setSummeSelectedRechnungen,
  setBeinhaltetNichtFreigegebeneRechnungen,
  setAlleSelectedRechnungen,
  setZahllaufZuweisenModalOpened,
  zahllaufZuweisenModalOpened,
  setZahlungsdateiGenerierenModalOpened,
  zahlungsdateiGenerierenModalOpened,
  alleRechnungen,
  ...input
}: ZahllaufSummenProps & {
  setZahllaufZuweisenModalOpened: (value: boolean) => void;
  zahllaufZuweisenModalOpened: boolean;
  setZahlungsdateiGenerierenModalOpened: (value: boolean) => void;
  zahlungsdateiGenerierenModalOpened: boolean;
} & ZahllaufProps) {
  const [filteredState, setFilteredState] = useState("");
  const request = useDataRequest<ZahllaufFilter>({
    filter: { zahllaufBezahlt: false },
    ...input,
  });

  function assignmentModalOpen() {
    setZahllaufZuweisenModalOpened(!zahllaufZuweisenModalOpened);
    setZahlungsdateiGenerierenModalOpened(false);
  }

  function generationModalOpen() {
    setZahlungsdateiGenerierenModalOpened(!zahlungsdateiGenerierenModalOpened);
    setZahllaufZuweisenModalOpened(false);
  }
  const { t: translation } = useT("zahllauf");
  return (
    <Stack spacing={2}>
      <ZahllaufFilterBar request={request} filteredStateFilter={filteredState}>
        <Guard permission={"zahllauf:archiv:view"}>
          <Button
            variant="contained"
            component="label"
            color={"secondary"}
            onClick={generationModalOpen}
          >
            {capitalize(translation("generate"))}
          </Button>
        </Guard>
        <Guard permission={"zahllauf:create"}>
          <ZahllaufSplitbutton
            assignmentModalOpen={assignmentModalOpen}
            alleRechnungen={alleRechnungen}
          />
        </Guard>
      </ZahllaufFilterBar>
      <ZahllaufResults
        setAnzahlAlleRechnungen={setAnzahlAlleRechnungen}
        setSummeAlleRechnungen={setSummeAlleRechnungen}
        setAnzahlSelectedRechnungen={setAnzahlSelectedRechnungen}
        setSummeSelectedRechnungen={setSummeSelectedRechnungen}
        setBeinhaltetNichtFreigegebeneRechnungen={
          setBeinhaltetNichtFreigegebeneRechnungen
        }
        setAlleSelectedRechnungen={setAlleSelectedRechnungen}
        request={request}
        setFilteredState={setFilteredState}
      />
    </Stack>
  );
}

function ZahllaufResults({
  setAnzahlAlleRechnungen,
  setSummeAlleRechnungen,
  setAnzahlSelectedRechnungen,
  setSummeSelectedRechnungen,
  setBeinhaltetNichtFreigegebeneRechnungen,
  setAlleSelectedRechnungen,
  request,
  setFilteredState,
}: ZahllaufSummenProps & {
  request: DataRequestState<ZahllaufFilter>;
  setFilteredState: (value: string) => void;
}) {
  const zahllaufprops = useMemo(
    () => ({
      setAnzahlAlleRechnungen,
      setSummeAlleRechnungen,
      setAnzahlSelectedRechnungen,
      setSummeSelectedRechnungen,
      setBeinhaltetNichtFreigegebeneRechnungen,
      setAlleSelectedRechnungen,
    }),
    [
      setAnzahlAlleRechnungen,
      setSummeAlleRechnungen,
      setAnzahlSelectedRechnungen,
      setSummeSelectedRechnungen,
      setBeinhaltetNichtFreigegebeneRechnungen,
      setAlleSelectedRechnungen,
    ]
  );
  return (
    <Results
      request={request}
      setFilteredState={setFilteredState}
      zahllaufprops={zahllaufprops}
    />
  );
}

function Results({
  zahllaufprops,
  request,
  setFilteredState,
}: {
  zahllaufprops: ZahllaufSummenProps;
  request: DataRequestState<ZahllaufFilter>;
  setFilteredState: (value: string) => void;
}) {
  const getApi = useGetApi();
  const columns = useZahllaufColumns();

  const [rechnungenIds, setRechnungenIds] = useState<string[]>([]);
  const { serverKeyVersion } = useZahllaufContext();
  const [filterModel, setFilterModel] = useState<ColumnFilter[]>([]);
  const navigate = useNavigate();
  const [sortModel, setSortModel] = useState<SortModel | null>(null);
  const [visibility, setVisibility] = useState<ColumnVisibilityModel>(() => {
    const model = Object.fromEntries(
      columns
        .filter((c: ColumnDef<EingangsrechnungInfo>) => c.hidden)
        .map((c: ColumnDef<EingangsrechnungInfo>) => [c.id, false])
    );
    model["gesamtGutschrift"] = false;
    return model;
  });

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: request.limit ?? 200,
  });

  const filterKey = JSON.stringify(request.filter);
  useEffect(() => {
    setRechnungenIds([]);
  }, [serverKeyVersion, filterKey, pagination.page, pagination.pageSize]);

  const onPageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const onPageSizeChange = useCallback(
    (pageSize: number) => {
      setPagination((prev) => ({ ...prev, pageSize }));
      request.setLimit(pageSize);
    },
    [request]
  );

  const onFilterModelChange = useCallback((model: ColumnFilter[]) => {
    setFilterModel(model);
  }, []);

  const onSortModelChange = useCallback((model: SortModel | null) => {
    setSortModel(model);
  }, []);

  const { data: alleRechnungen } = useQuery(
    [
      "zahllauf",
      request.params,
      pagination.page,
      pagination.pageSize,
      filterModel,
      sortModel,
    ],
    async () => {
      const api = await getApi();
      const mappedFilters = filterModel.map((f) => ({
        field: f.columnId,
        operator: f.operator,
        value: f.value,
      }));
      return await api.rechnungen.sucheEingangsrechnungen({
        ...request.params,
        page: pagination.page,
        limit: pagination.pageSize,
        sort:
          sortModel?.direction === "desc"
            ? "-" + sortModel.columnId
            : sortModel?.columnId,
        filtered: JSON.stringify(mappedFilters),
      });
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );

  const zahllaufDataSource = useCallback(
    async (query: QueryOptions): Promise<QueryResult<EingangsrechnungInfo>> => {
      const mappedFilters = (query.filters ?? []).map((f) => ({
        field: f.columnId,
        operator: f.operator,
        value: f.value,
      }));

      const api = await getApi();
      const result = await api.rechnungen.sucheEingangsrechnungen({
        ...request.params,
        page: query.page,
        limit: query.pageSize,
        sort:
          query.sort?.direction === "desc"
            ? "-" + query.sort.columnId
            : query.sort?.columnId,
        filtered: JSON.stringify(mappedFilters),
      });

      return {
        rows: result.items ?? [],
        total: result.totalElements ?? 0,
      };
    },
    [getApi, request.params]
  );

  useEffect(() => {
    const mappedFilters = filterModel.map((f) => ({
      field: f.columnId,
      operator: f.operator,
      value: f.value,
    }));
    setFilteredState(JSON.stringify(mappedFilters));
  }, [filterModel, setFilteredState]);

  useEffect(() => {
    const anzahlAllerRechnungen = alleRechnungen?.totalElements ?? 0;
    const summeAllerRechnungen = alleRechnungen?.items.reduce(
      (acc, aktuellerWert) => acc + (aktuellerWert?.rechnungsBetrag ?? 0),
      0
    );
    const anzahlAusgewaehlterRechnungen = rechnungenIds.length;
    const summeAusgewaehlterRechnungen = alleRechnungen?.items
      .filter((rechnung) => rechnungenIds.includes(rechnung.id))
      .reduce(
        (acc, aktuellerWert) => acc + (aktuellerWert?.rechnungsBetrag ?? 0),
        0
      );

    const nichtAlleFreigegeben = alleRechnungen?.items
      .filter((rechnung) => rechnungenIds.includes(rechnung.id))
      .some(
        (rechnung) =>
          rechnung.belegStatus !== "FREIGEGEBEN" &&
          rechnung.belegStatus !== "IN_DATEV"
      );

    zahllaufprops.setAnzahlAlleRechnungen(anzahlAllerRechnungen ?? 0);
    zahllaufprops.setSummeAlleRechnungen(summeAllerRechnungen ?? 0);
    zahllaufprops.setAnzahlSelectedRechnungen(
      anzahlAusgewaehlterRechnungen ?? 0
    );
    zahllaufprops.setSummeSelectedRechnungen(summeAusgewaehlterRechnungen ?? 0);
    if (zahllaufprops.setBeinhaltetNichtFreigegebeneRechnungen) {
      zahllaufprops.setBeinhaltetNichtFreigegebeneRechnungen(
        nichtAlleFreigegeben ?? true
      );
    }
    zahllaufprops.setAlleSelectedRechnungen(rechnungenIds ?? []);
  }, [rechnungenIds, alleRechnungen, zahllaufprops]);

  return (
    <RomeisDataGrid<EingangsrechnungInfo>
      columns={columns}
      rows={undefined}
      dataSource={zahllaufDataSource}
      getRowId={(row) => row.id}
      selectionMode={"multi"}
      selectionModel={rechnungenIds}
      pageSize={pagination.pageSize}
      paginationMode={"server"}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSelectionChange={(newSelection) => {
        setRechnungenIds(newSelection as string[]);
      }}
      columnVisibilityModel={visibility}
      onColumnVisibilityModelChange={setVisibility}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      csvOptions={{ fileName: "zahllauf.csv" }}
      printOptions={{ title: "Zahlläufe", allPages: true }}
      showToolbar={false}
      pageSizeOptions={[50, 100, 200, 300, 500]}
      queryKey={[serverKeyVersion, request.params]}
      labels={germanLabels}
      onRowClick={(row) => {
        if (row.event.ctrlKey || row.event.metaKey) {
          window.open(`/eingangsrechnungen/${row.id}`, "_blank");
        } else {
          navigate(`/eingangsrechnungen/${row.id}`);
        }
      }}
      hidePivotToggle={true}
    />
  );
}
